import { SEED_COSTS, useSeedBalance, useSeedHistory, useFillSilo, EMMA_FREE_PER_WEEK } from '@/hooks/useSeeds'
import { usePlan, useRegions, useTopics } from '@/hooks/useConfig'
import { useStorageUsed } from '@/hooks/useRecordings'
import type { SeedLedgerEntry } from '@/types/database'
import '@/styles/seeds.css'

/**
 * The rate card, the balance, and the store.
 *
 * Pricing is transparent by construction: every cost is published here, and
 * every button elsewhere in the app repeats its own price before it is tapped.
 */

const RATE_CARD: { label: string; cost: number | 'free'; note: string }[] = [
  { label: 'Browsing, sorting, filtering', cost: 'free', note: 'Navigation is not consumption' },
  { label: 'First headline pull on a place', cost: 'free', note: 'Once per place' },
  { label: 'Reroll for more headlines', cost: SEED_COSTS.reroll, note: 'A fresh batch' },
  { label: 'Build a story', cost: SEED_COSTS.build_story, note: 'Article and script' },
  { label: 'Regenerate an article or script', cost: SEED_COSTS.regenerate, note: 'Redo one part' },
  { label: 'Build a story in another voice', cost: SEED_COSTS.voice_build, note: 'Switching back to one you own is free' },
  { label: 'Research a keyword', cost: SEED_COSTS.pill_research, note: 'Only when Emma answers' },
  { label: 'Add a place', cost: SEED_COSTS.add_region, note: 'Free during setup' },
  { label: 'Add, remove or move topics', cost: 'free', note: 'Your mix is yours to tune' },
  { label: 'Reset your topic mix', cost: SEED_COSTS.reset_mix, note: 'Evens it out and clears locks' },
  { label: 'Make the newsletter version of a story', cost: SEED_COSTS.newsletter_version, note: 'A title and a short blurb for this week’s issue' },
  { label: 'A breaking alert', cost: SEED_COSTS.breaking_alert, note: 'From the alert budget, not your week' },
  { label: 'Ask Emma', cost: 'free', note: `${EMMA_FREE_PER_WEEK} questions a week included, then 1 seed each` },
]

// Pack sizes are not final; the reference deliberately leaves them open until
// they line up with the purchase milestones.
const PACKS = [
  { tier: 'bronze', name: 'Seed pack', seeds: 100, blurb: 'A single sack. For topping up a week that ran short.' },
  { tier: 'silver', name: 'Seed pack', seeds: 300, blurb: 'Sack plus a stack. For publishing past your plan most weeks.' },
  { tier: 'gold',   name: 'Seed pack', seeds: 600, blurb: 'Silo and full stock. Bulk, at the best rate per seed.' },
] as const

function actionLabel(e: SeedLedgerEntry): string {
  switch (e.action) {
    case 'build_story': return 'Built a story'
    case 'regenerate':  return 'Regenerated'
    case 'reroll':      return 'Rerolled headlines'
    case 'reformat':    return 'Reformatted'
    case 'add_region':  return 'Added a place'
    case 'swap_topic':  return 'Changed a topic'
    case 'reset_mix':   return 'Reset the topic mix'
    case 'voice_build': return 'Built another voice'
    case 'pill_research': return 'Researched a keyword'
    case 'advisor_question': return 'Asked Emma'
    case 'advisor_topup': return e.kind === 'silo' ? 'Filled the silo' : 'Moved to the silo'
    case 'purchase':    return 'Bought seeds'
    case 'trial_grant': return 'Trial seeds'
    case 'reward_grant': return 'Reward seeds'
    default:            return 'Seeds granted'
  }
}

/** Renders bytes at a scale a person can read: a 26MB library should not
 *  display as "0 GB". */
function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 MB'
  const gb = bytes / 1024 ** 3
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  const mb = bytes / 1024 ** 2
  if (mb >= 1) return `${Math.round(mb)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** One capacity line: used against the plan cap, with a bar. */
function UsageRow({ label, used, cap, unit, note, display }: {
  label: string; used: number; cap: number; unit?: string; note: string
  /** Overrides the "used" text when a raw number would not read well. */
  display?: string
}) {
  // A cap of zero means the plan has not loaded yet. Showing "3 of 0" would
  // read as being over the limit, so hold the bar back until it is known.
  const known = cap > 0
  const pct = known ? Math.min(100, Math.round((used / cap) * 100)) : 0
  const full = known && used >= cap
  return (
    <div className="usage">
      <div className="usage__top">
        <span className="usage__label">{label}</span>
        <span className={`usage__count ${full ? 'is-full' : ''}`}>
          {display ?? `${used}${unit ? ` ${unit}` : ''}`} of {known ? cap : '...'}{unit ? ` ${unit}` : ''}
        </span>
      </div>
      <div className="usage__bar" role="img"
           aria-label={`${label}: ${used} of ${known ? cap : 'unknown'} used`}>
        <div className={`usage__fill ${full ? 'is-full' : ''}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="usage__note">{note}</span>
    </div>
  )
}

export function SeedPanel() {
  const { data: balance } = useSeedBalance()
  const { data: plan } = usePlan()
  const { data: history } = useSeedHistory(25)
  const { data: regions } = useRegions()
  const { data: topics } = useTopics()
  const { data: storageBytes } = useStorageUsed()
  const fill = useFillSilo()

  const weekly = balance?.weekly_remaining ?? 0
  const allowance = balance?.seeds_week ?? plan?.seeds_week ?? 0
  const pct = allowance > 0 ? Math.round((weekly / allowance) * 100) : 0

  return (
    <div className="settings-section seeds">
      <h2>Seeds</h2>
      <p className="settings-hint">
        Seeds are what you spend to build and refine stories. Your weekly allowance
        refills at the start of your week. Anything you buy on top never expires.
      </p>

      <div className="seeds__balances">
        <div className="seeds__bal">
          <span className="seeds__ballabel">This week</span>
          <span className="seeds__balvalue">{weekly}</span>
          <span className="seeds__balnote">of {allowance}, refills weekly</span>
          <div className="seeds__bar" role="img"
               aria-label={`${weekly} of ${allowance} weekly seeds remaining`}>
            <div className="seeds__barfill" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="seeds__bal">
          <span className="seeds__ballabel">Purchased</span>
          <span className="seeds__balvalue">{balance?.purchased_remaining ?? 0}</span>
          <span className="seeds__balnote">never expires</span>
        </div>
        <div className="seeds__bal">
          <span className="seeds__ballabel">Gifted</span>
          <span className="seeds__balvalue">{balance?.granted_remaining ?? 0}</span>
          <span className="seeds__balnote">from coins or your trial</span>
        </div>
      </div>

      <h3 className="seeds__h3">Emma&rsquo;s silo</h3>
      <div className="gg-panel seeds__silo">
        <div className="seeds__silotop">
          <span className="seeds__balvalue">{balance?.silo_remaining ?? 0}<small> / {balance?.silo_capacity ?? 0}</small></span>
          <span className="gg-help">
            {balance?.emma_free_remaining ?? 0}{(balance?.emma_free_remaining ?? 0) > EMMA_FREE_PER_WEEK ? '' : ` of ${EMMA_FREE_PER_WEEK}`} questions left this week. After those, each
            question is one seed: from the silo while it holds any, then from your other seeds. The silo fills from purchased seeds.
          </span>
        </div>
        <div className="seeds__bar" role="img" aria-label="Silo level">
          <div className="seeds__barfill" style={{ width: `${balance && balance.silo_capacity ? Math.round((balance.silo_remaining / balance.silo_capacity) * 100) : 0}%` }} />
        </div>
        <button className="gg-btn gg-btn--sm" disabled={fill.isPending || !balance || balance.silo_remaining >= balance.silo_capacity || balance.purchased_remaining < 1}
          onClick={() => fill.mutate()}>
          {fill.isPending ? 'Filling' : balance && balance.silo_remaining >= balance.silo_capacity ? 'The silo is full' : 'Fill from purchased seeds'}
        </button>
        {fill.data != null && !fill.isPending && <p className="gg-ok">Moved {fill.data} seeds into the silo.</p>}
        {fill.error && <p className="gg-error">{(fill.error as Error).message}</p>}
      </div>

      <p className="seeds__order">
        Spending always takes this week&rsquo;s seeds first, so nothing you paid for is
        burned while free seeds are still expiring.
      </p>

      <h3 className="seeds__h3">Your plan</h3>
      <div className="seeds__usage">
        <UsageRow
          label="Places"
          used={regions?.length ?? 0}
          cap={plan?.max_regions ?? 0}
          note="Towns, counties or states you cover"
        />
        <UsageRow
          label="Topics"
          used={topics?.length ?? 0}
          cap={plan?.max_topics ?? 0}
          note="Subjects your headlines lean toward"
        />
        <UsageRow
          label="Storage"
          // The bar needs the real fraction, so keep GB for the maths and give
          // the label its own readable form.
          used={(storageBytes ?? 0) / 1024 ** 3}
          display={formatBytes(storageBytes ?? 0)}
          cap={plan?.storage_gb ?? 0}
          unit="GB"
          note="Recordings kept in your library"
        />
      </div>

      <h3 className="seeds__h3">What things cost</h3>
      <div className="seeds__tablewrap">
        <table className="seeds__table">
          <tbody>
            {RATE_CARD.map((row) => (
              <tr key={row.label}>
                <td>
                  {row.label}
                  <span className="seeds__rownote">{row.note}</span>
                </td>
                <td className="seeds__cost">
                  {row.cost === 'free'
                    ? <span className="seeds__free">Free</span>
                    : row.cost}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="seeds__h3">Top up</h3>
      <div className="seeds__packs">
        {PACKS.map((p) => (
          <div key={p.tier} className={`seeds__pack seeds__pack--${p.tier}`}>
            <img
              src={`/seeds/pack-${p.tier}.png`}
              srcSet={`/seeds/pack-${p.tier}.png 1x, /seeds/pack-${p.tier}@2x.png 2x`}
              alt=""
              width={96}
              height={96}
              loading="lazy"
            />
            <span className="seeds__packtier">{p.tier}</span>
            <span className="seeds__packseeds">{p.seeds} seeds</span>
            <p className="seeds__packblurb">{p.blurb}</p>
            {/* Purchasing is not wired up yet. A button that looks live and then
                does nothing is worse than one that says so. */}
            <button className="btn btn--ghost" disabled>Not available yet</button>
          </div>
        ))}
      </div>
      <p className="settings-hint">
        Buying seeds is not switched on yet. If you run short in the meantime, get in
        touch and we will top you up.
      </p>

      <h3 className="seeds__h3">Recent activity</h3>
      {!history || history.length === 0 ? (
        <p className="settings-hint">Nothing spent yet.</p>
      ) : (
        <ul className="seeds__history">
          {history.map((e) => (
            <li key={e.id}>
              <span className="seeds__hact">{actionLabel(e)}</span>
              <span className="seeds__hdate">
                {new Date(e.created_at).toLocaleDateString(undefined,
                  { month: 'short', day: 'numeric' })}
              </span>
              <span className={`seeds__hdelta ${e.delta > 0 ? 'is-up' : ''}`}>
                {e.delta > 0 ? `+${e.delta}` : e.delta}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
